import { useMemo } from "react";
import type { PaymentTransaction } from "../../../domain/entities/PaymentTransaction";
import type { PaymentMethod } from "../../../domain/entities/PaymentMethod";
import { checkoutCopy, checkoutLocale } from "../../i18n/checkout/es";
import { ReturnToAppButton } from "../../components/common/ReturnToAppButton";
import styles from "./PaymentStatusPage.module.css";

const formatDateTime = (date: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

interface PaymentProcessingPageProps {
  transaction: PaymentTransaction;
  paymentMethodName: string;
  paymentMethod?: PaymentMethod | null;
  bookingCode?: string;
}

export const PaymentProcessingPage = ({
  transaction,
  paymentMethodName,
  paymentMethod,
  bookingCode,
}: PaymentProcessingPageProps) => {
  const updatedAt = useMemo(() => formatDateTime(transaction.updatedAt, checkoutLocale), [transaction.updatedAt]);
  const methodLabel = paymentMethod?.name ?? paymentMethodName ?? checkoutCopy.status.unknownMethodName;

  return (
    <div className={styles.page} role="main">
      <section className={styles.card} aria-labelledby="payment-processing-title">
        <div className={`${styles.statusIcon} ${styles.processingIcon}`} aria-hidden="true" />
        <header className={styles.statusHeader}>
          <h1 id="payment-processing-title" className={styles.statusTitle}>
            {checkoutCopy.status.processing.title}
          </h1>
          <p className={styles.statusDescription}>{checkoutCopy.status.processing.description}</p>
        </header>

        <dl className={styles.statusDetails}>
          <div className={styles.detailRow}>
            <dt className={styles.detailLabel}>{checkoutCopy.status.processing.referenceLabel}</dt>
            <dd className={styles.detailValue}>{transaction.referenceCode}</dd>
          </div>
          <div className={styles.detailRow}>
            <dt className={styles.detailLabel}>{checkoutCopy.status.processing.methodLabel}</dt>
            <dd className={styles.detailValue}>{methodLabel}</dd>
          </div>
          <div className={styles.detailRow}>
            <dt className={styles.detailLabel}>{checkoutCopy.status.processing.updatedAtLabel}</dt>
            <dd className={styles.detailValue}>{updatedAt}</dd>
          </div>
        </dl>

        <div className={styles.actions}>
          <ReturnToAppButton label={checkoutCopy.status.return.action} bookingCode={bookingCode} />
          <p className={styles.returnHint}>{checkoutCopy.status.return.hint}</p>
        </div>
      </section>
    </div>
  );
};
